import { Form, Formik } from "formik";
import React, {
  useEffect,
  useState,
  forwardRef,
  useRef,
  useImperativeHandle,
} from "react";
import Input from "../../../../components/common/inputs/Input";
import Select from "../../../../components/common/inputs/Select";
import Button from "../../../../components/common/button/Button";
import ImagesModal from "../../../../components/common/imgaes-modal/ImagesModal";
import { publicImageProps } from "../../../../services/config";
import { courseSectionValidation } from "../../../../components/common/validation/course/courseSectionValidation";

import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const renumber = (items) => items.map((item, index) => ({ ...item, part: index + 1 }));

function SortableSectionItem({ item, onEdit, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: item.sortId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="admin-course-section-card p-5 rounded-xl shadow-lg bg-white border border-gray-200 flex justify-between items-center hover:shadow-xl transition-shadow duration-300"
    >
      {/* دستگیره درگ */}
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="touch-none cursor-grab p-3 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors duration-200 select-none"
        aria-label={`جابه‌جایی قسمت ${item.part}`}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-6 w-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M4 8h16M4 16h16" />
        </svg>
      </button>

      {/* بخش اطلاعات آیتم */}
      <div className="flex gap-5 items-center flex-1 overflow-hidden">
        {item.image && (
          <img
            {...publicImageProps(item.image, { variant: "thumb_" })}
            alt={item.name}
            className="w-16 h-16 rounded-lg object-cover border border-gray-300 shadow-sm"
          />
        )}
        <div className="flex flex-col gap-1 text-gray-700 overflow-hidden">
          <h3 className="font-extrabold text-lg truncate">{item.name}</h3>
          <p className="text-sm text-gray-500 truncate">
            <strong>آدرس: </strong> {item.url || "-"}
          </p>
          <p className="text-sm text-gray-500 truncate">
            <strong>قسمت: </strong> {item.part || "-"}
          </p>
          <p className="text-sm text-gray-500 line-clamp-2">
            <strong>توضیحات: </strong> {item.description || "-"}
          </p>
          <p className="text-sm text-gray-500 truncate">
            <strong>توضیحات کوتاه: </strong> {item.shortDescription || "-"}
          </p>
          <p>
            <strong>رایگان: </strong>
            {item.isFree ? (
              <span className="text-green-600 font-semibold">بله</span>
            ) : (
              <span className="text-red-600 font-semibold">خیر</span>
            )}
          </p>
        </div>
      </div>

      {/* دکمه‌ها */}
      <div className="flex gap-3">
        <Button
          type="button"
          color="primary"
          text="ویرایش"
          onClick={() => onEdit(item)}
          className="px-4 py-2 rounded-lg font-semibold shadow-md hover:shadow-lg transition"
        />
        <Button
          type="button"
          color="danger"
          text="حذف"
          onClick={() => onDelete(item)}
          className="px-4 py-2 rounded-lg font-semibold shadow-md hover:shadow-lg transition"
        />
      </div>
    </div>
  );
}


const CourseSection = forwardRef(({ sectionsData, handleSections }, ref) => {
  const [showModal, setShowModal] = useState(false);
  const [section, setSection] = useState(null);
  const [sections, setSections] = useState([]);
  const [image, setImage] = useState("");
  const imagesModal = useRef();

  const nextId = useRef(0);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useImperativeHandle(ref, () => ({
    showModal() {
      setShowModal(!showModal);
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    afterSubmit() {
      setSection(null);
      setSections([]);
      setImage("");
    },
  }));

  const formHandler = (values, { resetForm }) => {
    const newSection = {
      ...section,
      ...values,
      sortId: section?.sortId || `new-section-${++nextId.current}`,
      image: image || "",
      part: section?.part || sections.length + 1,
    };

    if (!section) {
      setSections((prev) => renumber([...prev, newSection]));
    } else {
      setSections((prev) =>
        renumber(prev.map((s) => (s.sortId === section.sortId ? newSection : s)))
      );
      setSection(null);
    }

    setImage("");
    resetForm();
  };

  const onDelete = (data) => {
    console.log(data);

    setSections((prev) => renumber(prev.filter((item) => item.sortId !== data.sortId)));
  };

  const onEdit = (data) => {
    setSection(data);
    setImage(data.image);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleNewSections = () => {
    handleSections(renumber(sections).map(({ sortId, id, ...item }) => item));
    setShowModal(false);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setSections((items) => {
      const oldIndex = items.findIndex((item) => item.sortId === active.id);
      const newIndex = items.findIndex((item) => item.sortId === over.id);
      return oldIndex < 0 || newIndex < 0 ? items : renumber(arrayMove(items, oldIndex, newIndex));
    });
  };

  const selectedImages = (item) => {
    setImage(item.name);
  };

  useEffect(() => {
    const ordered = [...(sectionsData || [])].sort((a, b) => Number(a.part) - Number(b.part));
    setSections(renumber(ordered.map((item) => ({
      ...item,
      sortId: item._id || `new-section-${++nextId.current}`,
    }))));
  }, [sectionsData]);

  return (
    <>
      <div
        className={`admin-edit-backdrop fixed top-0 right-0 w-full h-full transition-all z-30 ${
          !showModal && "hidden"
        }`}
      ></div>
      <div
        hidden={!showModal}
        role="dialog"
        aria-label="سرفصل‌های دوره"
        aria-modal="true"
        className={`admin-edit-drawer w-full h-full overflow-y-scroll bg-white shadow-md z-40 rounded-3xl p-4 transition-all absolute top-0 -left-[120%] ${
          showModal && "!left-0 is-open"
        }`}
      >
        <div className="flex w-full items-center justify-between">
          <h4>اضافه کردن بخش</h4>
          <Button
            onClick={handleNewSections}
            type="button"
            color="primary"
            text="بازگشت"
          />
        </div>

        <Formik
          enableReinitialize
          initialValues={{
            name: section?.name || "",
            url: section?.url || "",
            part: section?.part || sections.length + 1,
            description: section?.description || "",
            shortDescription: section?.shortDescription || "",
            isFree: section?.isFree || false,
          }}
          validationSchema={courseSectionValidation}
          onSubmit={formHandler}
        >
          {(formik) => (
            <Form>
              <div className="grid gap-6 my-4 md:grid-cols-2">
                <Input name="name" placeholder="نام" label="نام" type="text" />
                <Input name="url" placeholder="آدرس" label="آدرس" type="text" />
                <Input
                  name="part"
                  placeholder="قسمت"
                  label="شماره قسمت (خودکار)"
                  type="number"
                  readOnly
                  hint="شماره قسمت با ترتیب جلسات تعیین می‌شود."
                />
                <Input
                  name="description"
                  placeholder="توضیحات"
                  label="توضیحات"
                  type="text"
                />
                <Input
                  name="shortDescription"
                  placeholder="توضیحات کوتاه"
                  label="توضیحات کوتاه"
                  type="text"
                />

                <Select
                  name="isFree"
                  value={formik.values["isFree"] ? "true" : "false"}
                  onChange={(e) => {
                    formik.setFieldValue("isFree", e.target.value === "true");
                  }}
                  placeholder="رایگان"
                  label="رایگان"
                >
                  <option value="true">بله</option>
                  <option value="false">خیر</option>
                </Select>

                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-900">
                    عکس
                  </label>
                  <div className="flex items-center justify-between">
                    <div className="flex gap-2">
                      {image && (
                        <img
                          {...publicImageProps(image, { variant: "thumb_" })}
                          className="w-16 h-16 rounded-lg"
                          alt=""
                        />
                      )}
                    </div>
                    <div className="flex gap-2">
                      {image && (
                        <Button
                          type="button"
                          onClick={() => setImage("")}
                          text={"حذف"}
                          color={"danger"}
                        />
                      )}
                      <Button
                        type="button"
                        onClick={() => imagesModal.current.showModal()}
                        text={image ? "ویرایش" : "انتخاب"}
                        color={"success"}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-2 flex flex-wrap gap-3">
                {section && (
                  <Button
                    type="button"
                    onClick={() => setSection(null)}
                    text={"بازگشت"}
                    color={"danger"}
                  />
                )}
                <Button text={section ? "ویرایش" : "افزون"} color={"primary"} />
              </div>
            </Form>
          )}
        </Formik>

        <div className="mt-8">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={sections.map((item) => item.sortId)}
              strategy={verticalListSortingStrategy}
            >
              <div className="flex flex-col gap-4">
                {sections.map((item) => (
                  <SortableSectionItem
                    key={item.sortId}
                    item={item}
                    onEdit={onEdit}
                    onDelete={onDelete}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      </div>

      <ImagesModal selectedImages={selectedImages} ref={imagesModal} />
    </>
  );
});

export default CourseSection;
